import React, { useState, useRef, useEffect } from 'react';
import './MonthYearPicker.css';

interface MonthYearPickerProps {
  value?: string; // Format: YYYY-MM
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
}

const MonthYearPicker: React.FC<MonthYearPickerProps> = ({ 
  value = '', 
  onChange, 
  label = 'Select Month & Year',
  required = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const pickerRef = useRef<HTMLDivElement>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 51 }, (_, i) => currentYear + i); // Current year + next 50 years

  useEffect(() => {
    if (value) {
      const [year, month] = value.split('-');
      setSelectedYear(parseInt(year));
      setSelectedMonth(parseInt(month) - 1);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMonthClick = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    const formattedValue = `${selectedYear}-${String(monthIndex + 1).padStart(2, '0')}`;
    onChange(formattedValue);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    const formattedValue = `${year}-${String(selectedMonth + 1).padStart(2, '0')}`;
    onChange(formattedValue);
  };

  const getDisplayValue = () => {
    if (!value) return 'Select Month & Year';
    return `${months[selectedMonth]} ${selectedYear}`;
  };

  return (
    <div className="month-year-picker" ref={pickerRef}>
      {label && (
        <label className="month-year-picker-label">
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </label>
      )}
      <div 
        className="month-year-picker-input"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{getDisplayValue()}</span>
        <span className="month-year-picker-icon">📅</span>
      </div>

      {isOpen && (
        <div className="month-year-picker-dropdown">
          <div className="month-year-picker-header">
            <select 
              className="year-selector"
              value={selectedYear}
              onChange={(e) => handleYearChange(parseInt(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="months-grid">
            {months.map((month, index) => (
              <div
                key={month}
                className={`month-item ${selectedMonth === index ? 'selected' : ''}`}
                onClick={() => handleMonthClick(index)}
              >
                {month.substring(0, 3)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
